import React, { useState } from 'react';
import {
    Search,
    AlertCircle,
    CheckCircle2,
    ArrowRightLeft,
    TrendingUp,
    Calendar,
} from 'lucide-react';
import { motion } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import HistorySection from '../history/HistorySection';

interface InstagramPost {
    url: string;
    thumbnail: string;
    likes: number;
    comments: number;
    views?: number;
    postedAt: string;
    type: 'image' | 'video' | 'carousel';
}

interface AuditData {
    success: boolean;
    profile: {
        username: string;
        fullName: string;
        bio: string;
        profilePic: string;
        followers: number;
        following: number;
        posts: number;
        isPrivate: boolean;
        isVerified: boolean;
    };
    engagement?: {
        averageLikes: number;
        averageComments: number;
        engagementRate: number;
    };
    recentPosts?: InstagramPost[];
    aiStrategy?: string;
}

const InstagramComparison: React.FC = () => {
    const [url1, setUrl1] = useState('');
    const [url2, setUrl2] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState('');
    const [comparisonData, setComparisonData] = useState<[AuditData, AuditData] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedMonth, setSelectedMonth] = useState('lifetime');
    const [postsLimit, setPostsLimit] = useState<number>(50);

    const handleCompare = async () => {
        if (!url1 || !url2) {
            setError('Please enter both Instagram profile URLs');
            return;
        }

        setLoading(true);
        setError(null);
        setLoadingStep('Initializing comparison engine...');

        try {
            // First URL
            setLoadingStep(`Analyzing @${url1.split('/').filter(Boolean).pop()}...`);
            const res1 = await fetch('/api/tools/instagram/audit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: url1, postsLimit }),
            });
            const data1 = await res1.json();

            // Second URL
            setLoadingStep(`Analyzing @${url2.split('/').filter(Boolean).pop()}...`);
            const res2 = await fetch('/api/tools/instagram/audit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: url2, postsLimit }),
            });
            const data2 = await res2.json();

            if (data1.success && data2.success) {
                setComparisonData([data1, data2]);
            } else {
                setError(data1.error || data2.error || 'Failed to analyze one or both profiles');
            }
        } catch (err: any) {
            setError(err.message || 'Comparison failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num: number) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    const getProxyUrl = (url: string) => url ? `/api/tools/instagram/proxy-image?url=${encodeURIComponent(url)}` : '';

    const filterPostsByMonth = (posts: InstagramPost[], month: string) => {
        if (month === 'lifetime') return posts;
        return posts.filter(p => {
            const date = new Date(p.postedAt);
            const m = date.toLocaleString('default', { month: 'short' }).toLowerCase();
            const y = date.getFullYear().toString();
            return `${m} ${y}` === month;
        });
    };

    const calculateMetrics = (data: AuditData, month: string) => {
        if (!data.recentPosts) return null;
        const filteredPosts = filterPostsByMonth(data.recentPosts, month);
        if (filteredPosts.length === 0) return { avgL: 0, avgC: 0, er: 0, count: 0 };

        const avgL = filteredPosts.reduce((acc, p) => acc + p.likes, 0) / filteredPosts.length;
        const avgC = filteredPosts.reduce((acc, p) => acc + p.comments, 0) / filteredPosts.length;
        const er = data.profile.followers > 0 ? ((avgL + avgC) / data.profile.followers) * 100 : 0;

        return {
            avgL: Math.round(avgL),
            avgC: Math.round(avgC),
            er: parseFloat(er.toFixed(2)),
            count: filteredPosts.length
        };
    };

    const months = [
        { id: 'lifetime', label: 'Lifetime' },
        { id: 'feb 2026', label: 'Feb 2026' },
        { id: 'jan 2026', label: 'Jan 2026' },
        { id: 'dec 2025', label: 'Dec 2025' }
    ];

    const CompareRow = ({ label, val1, val2, suffix = '', isBetter = (a: any, b: any) => a > b }: any) => {
        const better1 = isBetter(val1, val2);
        const better2 = isBetter(val2, val1);

        return (
            <div className="grid grid-cols-7 items-center py-4 border-b border-gray-50 last:border-0 group">
                <div className={`col-span-3 text-right pr-6 transition-all ${better1 ? 'scale-105' : 'opacity-60'}`}>
                    <span className={`text-xl font-black ${better1 ? 'text-[#216974]' : 'text-gray-900'}`}>
                        {typeof val1 === 'number' ? formatNumber(val1) : val1}{suffix}
                    </span>
                    {better1 && <TrendingUp size={14} className="inline ml-1 text-[#54c4b6] animate-bounce-subtle" />}
                </div>
                <div className="col-span-1 text-center">
                    <span className="text-[10px] font-black uppercase text-gray-400 tracking-widest">{label}</span>
                </div>
                <div className={`col-span-3 text-left pl-6 transition-all ${better2 ? 'scale-105' : 'opacity-60'}`}>
                    {better2 && <TrendingUp size={14} className="inline mr-1 text-[#54c4b6] animate-bounce-subtle" />}
                    <span className={`text-xl font-black ${better2 ? 'text-[#216974]' : 'text-gray-900'}`}>
                        {typeof val2 === 'number' ? formatNumber(val2) : val2}{suffix}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="text-center space-y-4">
                <div className="inline-flex p-3 bg-gradient-to-br from-[#216974] to-[#216974] rounded-2xl shadow-lg shadow-[#216974]">
                    <ArrowRightLeft size={32} className="text-white" />
                </div>
                <h1 className="text-4xl font-black text-gray-900 tracking-tight">Profile Comparison</h1>
                <p className="text-gray-500 text-lg max-w-lg mx-auto font-medium">Benchmark two accounts side-by-side to uncover competitive insights.</p>
            </div>

            <Card className="overflow-hidden border-0 shadow-2xl shadow-[#216974]">
                <div className="p-8 space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase text-gray-400 ml-2">Primary Profile</label>
                            <Input
                                placeholder="https://instagram.com/username1"
                                value={url1}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl1(e.target.value)}
                                className="text-lg h-14 pl-12 rounded-2xl"
                                leftIcon={<Search className="text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" size={20} />}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase text-gray-400 ml-2">Competitor Profile</label>
                            <Input
                                placeholder="https://instagram.com/username2"
                                value={url2}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl2(e.target.value)}
                                className="text-lg h-14 pl-12 rounded-2xl"
                                leftIcon={<Search className="text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" size={20} />}
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap">Posts per account</label>
                            <input
                                type="number"
                                min={1}
                                max={100}
                                value={postsLimit}
                                onChange={e => setPostsLimit(Math.max(1, Math.min(100, parseInt(e.target.value) || 50)))}
                                disabled={loading}
                                className="w-16 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-bold text-center text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#54c4b6] disabled:opacity-50"
                            />
                        </div>
                    </div>

                    <Button
                        onClick={handleCompare}
                        isLoading={loading}
                        className="w-full rounded-2xl h-14 bg-gradient-to-r from-[#216974] to-[#216974] hover:scale-[1.01] transition-all text-lg font-black"
                    >
                        {loading ? loadingStep : 'Start Deep Comparison'}
                    </Button>

                    {error && (
                        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-700">
                            <AlertCircle size={20} />
                            <p className="font-semibold">{error}</p>
                        </div>
                    )}
                </div>
            </Card>

            {comparisonData && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-1000">
                    {/* Month Selector */}
                    <div className="flex justify-center gap-2 bg-gray-100/50 p-1.5 rounded-2xl w-fit mx-auto border border-gray-100">
                        {months.map(m => (
                            <button
                                key={m.id}
                                onClick={() => setSelectedMonth(m.id)}
                                className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all ${selectedMonth === m.id ? 'bg-white text-[#216974] shadow-md ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>

                    {/* Comparison Dashboard */}
                    <Card className="border-0 shadow-2xl overflow-hidden bg-white">
                        {/* Headers */}
                        <div className="grid grid-cols-7 bg-gray-50/50 border-b border-gray-100">
                            <div className="col-span-3 p-8 flex flex-col items-center text-center">
                                <div className="relative group">
                                    <div className="absolute -inset-1 bg-gradient-to-tr from-[#54c4b6] to-[#e4c084] rounded-full blur opacity-40 group-hover:opacity-100 transition duration-1000"></div>
                                    <img src={getProxyUrl(comparisonData[0].profile.profilePic)} alt="" className="relative w-24 h-24 rounded-full border-4 border-white shadow-xl object-cover" />
                                </div>
                                <h3 className="mt-4 text-xl font-black text-gray-900 leading-tight">@{comparisonData[0].profile.username}</h3>
                                <p className="text-xs font-bold text-gray-400 mt-1 uppercase tracking-widest">{comparisonData[0].profile.fullName}</p>
                            </div>
                            <div className="col-span-1 border-x border-gray-100 flex items-center justify-center">
                                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg border border-gray-100 text-gray-400 font-black italic">VS</div>
                            </div>
                            <div className="col-span-3 p-8 flex flex-col items-center text-center">
                                <div className="relative group">
                                    <div className="absolute -inset-1 bg-gradient-to-tr from-[#216974] to-[#216974] rounded-full blur opacity-40 group-hover:opacity-100 transition duration-1000"></div>
                                    <img src={getProxyUrl(comparisonData[1].profile.profilePic)} alt="" className="relative w-24 h-24 rounded-full border-4 border-white shadow-xl object-cover" />
                                </div>
                                <h3 className="mt-4 text-xl font-black text-gray-900 leading-tight">@{comparisonData[1].profile.username}</h3>
                                <p className="text-xs font-bold text-gray-400 mt-1 uppercase tracking-widest">{comparisonData[1].profile.fullName}</p>
                            </div>
                        </div>

                        {/* Stats Matrix */}
                        <div className="px-8 py-4">
                            <CompareRow label="Followers" val1={comparisonData[0].profile.followers} val2={comparisonData[1].profile.followers} />
                            <CompareRow label="Posts" val1={comparisonData[0].profile.posts} val2={comparisonData[1].profile.posts} />

                            {/* Performance Metrics (Static for Lifetime, Dynamic for Months) */}
                            {(() => {
                                const m1 = calculateMetrics(comparisonData[0], selectedMonth);
                                const m2 = calculateMetrics(comparisonData[1], selectedMonth);

                                if (!m1 || !m2) return null;

                                return (
                                    <>
                                        <div className="py-6 flex items-center justify-center gap-3">
                                            <Calendar size={16} className="text-[#216974]" />
                                            <span className="text-xs font-black uppercase text-gray-400 tracking-[0.2em]">{selectedMonth} Performance</span>
                                        </div>
                                        <CompareRow label="Avg Likes" val1={m1.avgL} val2={m2.avgL} />
                                        <CompareRow label="Avg Comments" val1={m1.avgC} val2={m2.avgC} />
                                        <CompareRow label="Eng. Rate" val1={m1.er} val2={m2.er} suffix="%" />
                                        <CompareRow label="Posts Found" val1={m1.count} val2={m2.count} />
                                    </>
                                );
                            })()}

                            <div className="mt-10 grid grid-cols-2 gap-8 pb-8">
                                <div className="space-y-4">
                                    <h4 className="text-xs font-black uppercase text-gray-400 tracking-widest text-center">Engagement Strength</h4>
                                    <div className="h-4 bg-gray-100 rounded-full overflow-hidden flex">
                                        <div
                                            className="h-full bg-gradient-to-r from-[#54c4b6] to-[#216974] transition-all duration-1000"
                                            style={{ width: `${(comparisonData[0].engagement?.engagementRate || 0) / ((comparisonData[0].engagement?.engagementRate || 0) + (comparisonData[1].engagement?.engagementRate || 1)) * 100}%` }}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <h4 className="text-xs font-black uppercase text-gray-400 tracking-widest text-center">Audience Strength</h4>
                                    <div className="h-4 bg-gray-100 rounded-full overflow-hidden flex flex-row-reverse">
                                        <div
                                            className="h-full bg-gradient-to-l from-[#216974] to-[#216974] transition-all duration-1000"
                                            style={{ width: `${(comparisonData[1].profile.followers || 0) / ((comparisonData[0].profile.followers || 1) + (comparisonData[1].profile.followers || 0)) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Strategic AI Verdict - "Trained Logic" */}
                    {(comparisonData[0].aiStrategy || comparisonData[1].aiStrategy) && (
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            className="relative mt-8"
                        >
                            <div className="absolute -inset-1 bg-gradient-to-r from-[#e4c184] via-orange-600 to-[#e4c084] rounded-[2.5rem] blur opacity-25 group-hover:opacity-100 transition duration-1000"></div>
                            <Card className="relative border-0 shadow-2xl bg-white/95 backdrop-blur-3xl p-10 overflow-hidden">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-[#e4c184]/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                                
                                <div className="flex items-center gap-6 mb-10">
                                    <div className="w-20 h-20 rounded-[1.5rem] bg-gradient-to-br from-[#e4c184] to-[#e4c084] flex items-center justify-center shadow-2xl shadow-[#e4c184]/40">
                                        <TrendingUp size={36} className="text-white fill-current" />
                                    </div>
                                    <div>
                                        <h3 className="text-4xl font-black text-slate-900 tracking-tight italic">Strategic AI Verdict</h3>
                                        <p className="text-sm font-black text-[#e4c184] mt-2 uppercase tracking-[0.3em]">Competitive Gap Analysis Protocol</p>
                                    </div>
                                </div>

                                <div className="bg-slate-50/50 rounded-[2.5rem] p-12 border border-white shadow-inner whitespace-pre-wrap font-medium text-slate-800 leading-relaxed text-xl">
                                    {comparisonData[0].aiStrategy || comparisonData[1].aiStrategy || "Generating performance verdict..."}
                                    <div className="mt-12 pt-8 border-t border-slate-200/80 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full bg-[#e4c184] animate-pulse"></div>
                                            <span className="text-xs font-black uppercase text-slate-500 tracking-[0.4em]">Protocol: AI Strategic Reranking Complete</span>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </motion.div>
                    )}

                    {/* Insights Summary */}
                    <div className="grid md:grid-cols-2 gap-6">
                        <Card className="p-8 border-0 shadow-xl bg-gradient-to-br from-[#216974] to-white">
                            <h4 className="flex items-center gap-2 font-black text-[#216974] mb-4">
                                <TrendingUp size={20} />
                                Winning Profile
                            </h4>
                            <p className="text-gray-600 font-medium leading-relaxed">
                                {comparisonData[0].profile.followers > comparisonData[1].profile.followers && (comparisonData[0].engagement?.engagementRate || 0) > (comparisonData[1].engagement?.engagementRate || 0)
                                    ? `@${comparisonData[0].profile.username} dominates with both higher reach and engagement.`
                                    : (comparisonData[0].engagement?.engagementRate || 0) > (comparisonData[1].engagement?.engagementRate || 0)
                                        ? `@${comparisonData[0].profile.username} has a more loyal community despite having fewer followers.`
                                        : `@${comparisonData[1].profile.username} appears to be the stronger content creator in this matchup.`}
                            </p>
                        </Card>
                        <Card className="p-8 border-0 shadow-xl bg-gradient-to-br from-[#216974] to-white">
                            <h4 className="flex items-center gap-2 font-black text-[#216974] mb-4">
                                <CheckCircle2 size={20} />
                                Recommendation
                            </h4>
                            <p className="text-gray-600 font-medium leading-relaxed">
                                Focus on replicating content structures from {comparisonData[0].engagement!.engagementRate > comparisonData[1].engagement!.engagementRate ? `@${comparisonData[0].profile.username}` : `@${comparisonData[1].profile.username}`} which is seeing {Math.abs((comparisonData[0].engagement!.engagementRate - comparisonData[1].engagement!.engagementRate)).toFixed(1)}% higher interaction.
                            </p>
                        </Card>
                    </div>
                </div>
            )}

            <HistorySection toolId="ig-compare" />
        </div>
    );
};

export default InstagramComparison;
